import { useCallback, useEffect, useRef, useState } from 'react'

import { useGetMySocialMediaConnectionsLazyQuery } from 'graphql/generated/scheme'

const POLL_INTERVAL_MS = 3_000
const INSTAGRAM_SCHEME = 'instagram'

interface UseInstagramConnectionPollingOptions {
	/** Fired once when a connection with scheme "Instagram" (case-insensitive) appears. */
	onConnected: () => void
}

/**
 * Polls the lightweight social media connections query until an Instagram
 * connection appears (the connection is created asynchronously by a webhook
 * after the user sends the verification DM).
 *
 * Uses a manual setInterval + lazy query instead of `useQuery({ pollInterval })`
 * because of the known no-cache + StrictMode stuck-loading issue.
 */
export function useInstagramConnectionPolling({ onConnected }: UseInstagramConnectionPollingOptions) {
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const onConnectedRef = useRef(onConnected)
	onConnectedRef.current = onConnected
	const [isPolling, setIsPolling] = useState(false)

	const stop = useCallback(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}
		setIsPolling(false)
	}, [])

	const [fetchConnections] = useGetMySocialMediaConnectionsLazyQuery({
		fetchPolicy: 'no-cache',
		onCompleted: data => {
			// A response can land after stop() (e.g. the modal closed mid-flight) — bail instead of firing onConnected.
			if (!intervalRef.current) return
			const isConnected = data?.me?.socialMediaConnections?.some(
				connection => connection?.scheme?.toLowerCase() === INSTAGRAM_SCHEME
			)
			if (isConnected) {
				stop()
				onConnectedRef.current()
			}
		},
		onError: () => {
			// Intentionally silent: polling errors are transient and the next 3s tick retries.
			// Surfacing a toast here would spam the user every interval while offline.
		},
	})

	const start = useCallback(() => {
		if (intervalRef.current) clearInterval(intervalRef.current)
		intervalRef.current = setInterval(() => {
			fetchConnections()
		}, POLL_INTERVAL_MS)
		setIsPolling(true)
		// Immediate first check so an already-connected account resolves without waiting a full interval
		fetchConnections()
	}, [fetchConnections])

	// Clear the interval on unmount (tolerates StrictMode double effects — start() is idempotent)
	useEffect(() => stop, [stop])

	return { start, stop, isPolling }
}
